import React, { useState, useRef, useEffect } from 'react';
import { Box, Typography } from '@mui/material';
import { colors, fonts, sizes, spacing, radius } from '../../styles/designTokens';

const SearchableDropdown = ({ name, value, options = [], placeholder, onChange, disabled, searchText, noResultsText }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const wrapperRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(event) {
            if (wrapperRef.current && !wrapperRef.current.contains(event.target)) {
                setIsOpen(false);
                setSearchTerm("");
            }
        }
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const getLabel = (opt) => typeof opt === 'object' ? opt.label : opt;
    const getValue = (opt) => typeof opt === 'object' ? opt.value : opt;

    const filteredOptions = options.filter(opt =>
        getLabel(opt).toLowerCase().includes(searchTerm.toLowerCase())
    );

    const handleSelect = (opt) => {
        onChange({ target: { name, value: getValue(opt) } });
        setIsOpen(false);
        setSearchTerm("");
    };

    return (
        <Box ref={wrapperRef} sx={{ width: '100%', position: 'relative' }}>
            <Box
                onClick={() => !disabled && setIsOpen(!isOpen)}
                sx={{
                    boxSizing: 'border-box',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    width: '100%',
                    height: '48px',
                    borderRadius: radius.lg, // 24px
                    border: `2px solid ${colors.border}`,
                    backgroundColor: disabled ? '#F0F0F0' : colors.background_light,
                    padding: `0 ${spacing.xl}`, // 20px
                    cursor: disabled ? 'not-allowed' : 'pointer',
                    opacity: disabled ? 0.6 : 1,
                    fontFamily: fonts.poppins,
                    fontSize: sizes.small || '14px',
                    userSelect: 'none'
                }}
            >
                <Typography
                    sx={{
                        color: value ? colors.text_dark : 'rgba(109, 120, 109, 0.7)',
                        fontSize: 'inherit',
                        fontFamily: 'inherit'
                    }}
                >
                    {options.find(opt => getValue(opt) === value) ? getLabel(options.find(opt => getValue(opt) === value)) : placeholder}
                </Typography>
                <Box
                    sx={{
                        transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                        transition: 'transform 0.2s',
                        color: colors.text,
                        pointerEvents: 'none',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                        width: '12px',
                        height: '8px'
                    }}
                >
                    <svg width="12" height="8" viewBox="0 0 12 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <path d="M1 1.5L6 6.5L11 1.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                </Box>
            </Box>

            {isOpen && (
                <Box sx={{
                    position: 'absolute',
                    top: '100%',
                    left: 0,
                    right: 0,
                    mt: spacing.xs, // 4px
                    backgroundColor: colors.light,
                    border: `1px solid ${colors.border}`,
                    borderRadius: radius.md, // 16px
                    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
                    zIndex: 10,
                    display: 'flex',
                    flexDirection: 'column',
                    overflow: 'hidden'
                }}>
                    <Box sx={{ padding: '12px', borderBottom: `1px solid ${colors.border}` }}>
                        <Box
                            component="input"
                            type="text"
                            placeholder={searchText}
                            value={searchTerm}
                            autoFocus
                            onChange={(e) => setSearchTerm(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            sx={{
                                boxSizing: 'border-box',
                                width: '100%',
                                padding: '8px 12px',
                                border: `1px solid ${colors.border}`,
                                borderRadius: radius.sm, // 8px
                                fontFamily: fonts.poppins,
                                fontSize: sizes.small || '14px',
                                outline: 'none',
                                '&:focus': { borderColor: colors.primary }
                            }}
                        />
                    </Box>
                    <Box
                        component="ul"
                        sx={{
                            listStyle: 'none',
                            m: 0,
                            p: 0,
                            maxHeight: '200px',
                            overflowY: 'auto'
                        }}
                    >
                        {filteredOptions.length > 0 ? filteredOptions.map(opt => (
                            <Box
                                component="li"
                                key={getValue(opt)}
                                onClick={() => handleSelect(opt)}
                                sx={{
                                    padding: '12px 16px',
                                    fontSize: sizes.small || '14px',
                                    color: colors.text_dark,
                                    cursor: 'pointer',
                                    fontFamily: fonts.poppins,
                                    transition: 'background 0.2s',
                                    '&:hover': { backgroundColor: colors.background2 }
                                }}
                            >
                                {getLabel(opt)}
                            </Box>
                        )) : (
                            <Box
                                component="li"
                                sx={{
                                    padding: '12px 16px',
                                    fontSize: sizes.small || '14px',
                                    color: colors.text,
                                    textAlign: 'center',
                                    fontFamily: fonts.poppins
                                }}
                            >
                                {noResultsText || 'No results'}
                            </Box>
                        )}
                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default SearchableDropdown;
